import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeMode = 'dark' | 'light';
export type AccentColor = 'cyan' | 'blue' | 'purple' | 'green' | 'pink';
export type AnimationSpeed = 'slow' | 'normal' | 'fast';

interface SettingsData {
  theme: ThemeMode;
  accent: AccentColor;
  animations: boolean;
  particles: boolean;
  animationSpeed: AnimationSpeed;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    governance: boolean;
    racing: boolean;
    treasury: boolean;
    security: boolean;
  };
  privacy: {
    profileVisibility: 'public' | 'members' | 'private';
    showBalance: boolean;
    showActivity: boolean;
    allowMessages: boolean;
  };
  accessibility: {
    highContrast: boolean;
    reducedMotion: boolean;
    fontSize: 'small' | 'medium' | 'large';
  };
}

interface SettingsContextType {
  settings: SettingsData;
  updateSettings: (updates: Partial<SettingsData>) => void;
  resetSettings: () => void;
  applyTheme: () => void;
}

const defaultSettings: SettingsData = {
  theme: 'dark',
  accent: 'cyan',
  animations: true,
  particles: true,
  animationSpeed: 'normal',
  notifications: {
    email: true,
    push: true,
    sms: false,
    governance: true,
    racing: true,
    treasury: true,
    security: true
  },
  privacy: {
    profileVisibility: 'public',
    showBalance: true,
    showActivity: true,
    allowMessages: true
  },
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    fontSize: 'medium'
  }
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SettingsData>(() => {
    // Load settings from localStorage
    const saved = localStorage.getItem('sui-ndicate-settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  const updateSettings = (updates: Partial<SettingsData>) => {
    setSettings(prev => {
      const newSettings = { ...prev, ...updates };
      localStorage.setItem('sui-ndicate-settings', JSON.stringify(newSettings));
      return newSettings;
    });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem('sui-ndicate-settings');
  };

  const applyTheme = () => {
    const root = document.documentElement;
    
    // Apply theme mode
    root.setAttribute('data-theme', settings.theme);
    
    // Apply accent color
    root.setAttribute('data-accent', settings.accent);
    
    // Apply animation preferences
    root.setAttribute('data-animations', settings.animations.toString());
    root.setAttribute('data-particles', settings.particles.toString());
    root.setAttribute('data-animation-speed', settings.animationSpeed);
    
    // Apply accessibility settings
    root.setAttribute('data-high-contrast', settings.accessibility.highContrast.toString());
    root.setAttribute('data-reduced-motion', settings.accessibility.reducedMotion.toString());
    root.setAttribute('data-font-size', settings.accessibility.fontSize);
    
    // Apply CSS custom properties
    const accentColors = {
      cyan: { primary: '#00f5ff', secondary: '#0066ff', accent: '#00d4ff' },
      blue: { primary: '#3b82f6', secondary: '#1d4ed8', accent: '#60a5fa' },
      purple: { primary: '#8b5cf6', secondary: '#7c3aed', accent: '#a78bfa' },
      green: { primary: '#10b981', secondary: '#059669', accent: '#34d399' },
      pink: { primary: '#ec4899', secondary: '#db2777', accent: '#f472b6' }
    };
    
    const colors = accentColors[settings.accent];
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--secondary-color', colors.secondary);
    root.style.setProperty('--accent-color', colors.accent);
    
    // Apply theme-specific styles
    if (settings.theme === 'light') {
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f8fafc');
      root.style.setProperty('--text-primary', '#1e293b');
      root.style.setProperty('--text-secondary', '#64748b');
      root.style.setProperty('--border-color', '#e2e8f0');
    } else {
      root.style.setProperty('--bg-primary', '#0f172a');
      root.style.setProperty('--bg-secondary', '#1e293b');
      root.style.setProperty('--text-primary', '#ffffff');
      root.style.setProperty('--text-secondary', '#94a3b8');
      root.style.setProperty('--border-color', '#334155');
    }
  };

  // Apply theme when settings change
  useEffect(() => {
    applyTheme();
  }, [settings]);

  // Apply theme on mount
  useEffect(() => {
    applyTheme();
  }, []);

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSettings,
      resetSettings,
      applyTheme
    }}>
      {children}
    </SettingsContext.Provider>
  );
};
